import { Capacitor } from "@capacitor/core";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Eye, EyeOff, Home, ShieldCheck } from "lucide-react";
import {
  type ChangeEvent,
  type SyntheticEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { queryClient } from "../hooks/useDataCache";
import { ALL_HOUSEHOLDS_QUERY_KEY } from "../hooks/useHouseholds";
import { USER_HOUSEHOLD_ID_KEY } from "../hooks/useUser";
import { supabase } from "../lib/supabase";
import {
  clearFieldError,
  focusFirstInvalidField,
  isValidEmail,
  validateAuthForm,
} from "../lib/validation";
import {
  acceptInvitation,
  getInvitationByToken,
  type HouseholdInvitation,
} from "../services/invitationService";

// Calls the native-auth edge function for sign-in/sign-up on native platforms,
// bypassing Turnstile CAPTCHA which does not work inside Android/iOS WebViews.
async function nativeAuth(
  action: "signIn" | "signUp",
  email: string,
  password: string,
): Promise<{
  data: {
    user: { id: string; identities?: unknown[]; email?: string } | null;
    session: unknown | null;
  };
  error: { message: string } | null;
}> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const nativeSecret = import.meta.env.VITE_NATIVE_APP_SECRET;
  const response = await fetch(`${supabaseUrl}/functions/v1/native-auth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-native-secret": nativeSecret,
    },
    body: JSON.stringify({ action, email, password }),
  });
  const json = await response.json();
  if (!response.ok || json.error) {
    return {
      data: { user: null, session: null },
      error: { message: json.error ?? "Authentication failed." },
    };
  }
  return { data: json.data, error: null };
}

type FormErrors = {
  email?: string;
  password?: string;
  householdName?: string;
  global?: string;
};

type ViewMode = "signin" | "signup" | "forgotPassword";

// Authentication form component handling sign in, sign up, invitation acceptance, and household creation
export default function AuthForm({
  pendingInvitation = null,
  initialView,
}: {
  pendingInvitation?: HouseholdInvitation | null;
  initialView?: ViewMode;
}) {
  const [email, setEmail] = useState(pendingInvitation?.email ?? "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (initialView) return initialView;
    // If there's an invitation token in the URL or prop, default to sign-up
    // so new users see the account creation form immediately.
    const params = new URLSearchParams(window.location.search);
    return (params.get("invitation") ?? pendingInvitation?.token)
      ? "signup"
      : "signin";
  });
  const [invitation, setInvitation] = useState<HouseholdInvitation | null>(
    pendingInvitation,
  );
  // Token comes from the URL param or from the pendingInvitation prop
  const [invitationToken] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("invitation") ?? pendingInvitation?.token ?? null;
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [emailConfirmationPending, setEmailConfirmationPending] =
    useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const errorId = useId();

  // Refs to inputs so we can focus the first invalid field on validation failure
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  useEffect(() => {
    if (invitationToken) {
      // If we already have the invitation from the prop, skip the fetch.
      if (pendingInvitation?.token === invitationToken) return;
      getInvitationByToken(invitationToken).then((inv) => {
        if (inv && inv.status === "pending") {
          setInvitation(inv);
          setEmail(inv.email);
          // Clear any errors when a valid invitation is loaded
          setFormErrors({});
        } else if (inv && inv.status !== "pending") {
          console.error("🔴 Invitation not pending - status:", inv.status);
          setFormErrors({
            global: `This invitation has already been ${inv.status}. Please contact the person who invited you.`,
          });
        } else {
          console.error("🔴 Invitation not found for token.");
          setFormErrors({
            global:
              "This invitation link is invalid or has expired. Please request a new invitation.",
          });
        }
      });
    }
  }, [invitationToken, pendingInvitation?.token]);

  // Reset the Turnstile widget whenever the app returns to the foreground.
  // On a PWA, the widget can be backgrounded for longer than the token's
  // 5-minute lifetime without firing onExpire, leaving a stale token in state
  // that Cloudflare rejects with "invalid-input-response".
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTurnstileToken(null);
        turnstileRef.current?.reset();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const handleViewChange = (newMode: ViewMode) => {
    setViewMode(newMode);
    setFormErrors({});
    setTurnstileToken(null);
    turnstileRef.current?.reset();
  };

  const handleAuth = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});

    const isSignUp = viewMode === "signup";

    // Validate inputs using helper
    const errors = validateAuthForm({
      email,
      password,
    });

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Use the shared focus helper and pass refs + explicit order
      focusFirstInvalidField(
        errors,
        { email: emailRef, password: passwordRef },
        ["email", "password"],
      );
      return;
    }

    setLoading(true);

    // Shared helper: accept an invitation, show a success toast, clear the URL,
    // switch the active household, and invalidate caches. Used by both the
    // signup-with-invitation and signin-with-invitation paths.
    const acceptAndSwitchHousehold = async (token: string, userId: string) => {
      const success = await acceptInvitation(token, userId);
      if (!success) {
        throw new Error("Failed to accept invitation.");
      }
      toast.success(`Welcome to the ${invitation?.household_name} household!`);
      window.history.replaceState({}, "", "/");
      if (invitation?.household_id) {
        localStorage.setItem(
          "pantry_active_household_id",
          String(invitation.household_id),
        );
      }
      void queryClient.invalidateQueries({
        queryKey: ALL_HOUSEHOLDS_QUERY_KEY,
      });
      void queryClient.invalidateQueries({
        queryKey: USER_HOUSEHOLD_ID_KEY,
      });
    };

    try {
      if (isSignUp) {
        if (!invitationToken) {
          // Create the account — Dashboard will show inline pricing if no license exists
          const { data, error } = Capacitor.isNativePlatform()
            ? await nativeAuth("signUp", email, password)
            : await supabase.auth.signUp({
                email,
                password,
                ...(turnstileToken
                  ? { options: { captchaToken: turnstileToken } }
                  : {}),
              });
          if (error) {
            if (error.message === "User already registered") {
              setViewMode("signin");
              toast(
                invitationToken
                  ? "Account already exists. Please sign in to join the household."
                  : "Account already exists. Please sign in.",
              );
              setLoading(false);
              return;
            }
            throw error;
          }

          // If user object exists but identities is empty, the user already exists
          // (Supabase enumeration protection). Switch to sign-in tab automatically.
          if (data.user && data.user.identities?.length === 0) {
            setViewMode("signin");
            toast(
              invitationToken
                ? "Account already exists. Please sign in to join the household."
                : "Account already exists. Please sign in.",
            );
            setLoading(false);
            return;
          }

          if ((data.user && !data.session) || (!data.user && !data.session)) {
            // Email confirmation is required — show "check your email" UI.
            // Supabase JS v2 returns data.user=null when confirmations are enabled,
            // so we treat any no-session response as "confirmation pending".
            setEmailConfirmationPending(true);
            setLoading(false);
            return;
          }

          if (!data.user || !data.session) {
            throw new Error("Failed to create account");
          }

          // Auth state change will redirect to Dashboard, which gates on license status
          setLoading(false);
          return;
        }

        // Invitation path: create account then accept invite
        const { data, error } = Capacitor.isNativePlatform()
          ? await nativeAuth("signUp", email, password)
          : await supabase.auth.signUp({
              email,
              password,
              ...(turnstileToken
                ? { options: { captchaToken: turnstileToken } }
                : {}),
            });
        if (error) {
          if (error.message === "User already registered") {
            setViewMode("signin");
            toast(
              "Account already exists. Please sign in to join the household.",
            );
            setLoading(false);
            return;
          }
          throw error;
        }

        // Existing account detected on invitation path — switch to sign-in tab.
        if (data.user && data.user.identities?.length === 0) {
          setViewMode("signin");
          toast(
            "Account already exists. Please sign in to join the household.",
          );
          setLoading(false);
          return;
        }

        if (data.user && data.session) {
          if (invitationToken) {
            await acceptAndSwitchHousehold(invitationToken, data.user.id);
            // The AuthGuard will automatically redirect to Dashboard once session is detected
          } else {
            // This shouldn't happen - signup without invitation should show paywall first
            console.error("🔴 Unexpected state: signed up without invitation");
            setLoading(false);
            return;
          }
        } else if (data.user && !data.session) {
          // Email confirmation required — persist token so AuthGuard can accept it after the
          // user clicks the confirmation link and is redirected back to the app.
          localStorage.setItem(
            "pantry_pending_invitation",
            JSON.stringify({
              token: invitationToken,
              householdName: invitation?.household_name ?? null,
              householdId: invitation?.household_id ?? null,
              email,
            }),
          );
          setEmailConfirmationPending(true);
          setLoading(false);
          return;
        }
      } else {
        const { data, error } = Capacitor.isNativePlatform()
          ? await nativeAuth("signIn", email, password)
          : await supabase.auth.signInWithPassword({
              email,
              password,
              ...(turnstileToken
                ? { options: { captchaToken: turnstileToken } }
                : {}),
            });
        if (error) throw error;

        // On native, the session comes back from our edge function rather than
        // the Supabase JS client, so we must set it explicitly on the local client.
        if (Capacitor.isNativePlatform() && data.session) {
          const session = data.session as {
            access_token: string;
            refresh_token: string;
          };
          await supabase.auth.setSession({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          });
        }

        // If there's an invitation token, accept it after sign in
        if (invitationToken && data.user) {
          await acceptAndSwitchHousehold(invitationToken, data.user.id);
        } else {
          toast.success("Welcome back!");
        }
      }

      // Don't set loading to false here - let the auth state change handle it
      // This prevents the form from flickering before the redirect
    } catch (err) {
      console.error("🔴 Auth error:", err);
      setFormErrors({
        global: err instanceof Error ? err.message : "Authentication failed.",
      });
      // Reset Turnstile so a fresh token is issued for the next attempt.
      // Without this, the consumed token causes a "timeout-or-duplicate" error.
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});

    if (!email.trim()) {
      setFormErrors({ email: "Please enter your email address." });
      emailRef.current?.focus();
      return;
    }

    if (!isValidEmail(email)) {
      setFormErrors({ email: "Please enter a valid email address." });
      emailRef.current?.focus();
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        "send-forgot-password-email",
        {
          body: { email: email.toLowerCase().trim() },
        },
      );

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to send password reset email.");
      }

      setResetEmailSent(true);
    } catch (err) {
      console.error("🔴 Forgot password error:", err);
      setFormErrors({
        global:
          err instanceof Error
            ? err.message
            : "Failed to send password reset email. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isSignUp = viewMode === "signup";
  const isForgotPassword = viewMode === "forgotPassword";

  const card = (
    <div className="relative w-full max-w-md rounded-lg bg-white/80 p-8 shadow-xl backdrop-blur-md dark:bg-gray-800/80">
      <ShieldCheck
        className="absolute top-3 right-3 h-4 w-4 text-gray-400 dark:text-gray-500"
        aria-hidden="true"
      />

      {invitation && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/30">
          <div className="mb-1 flex items-center gap-2 text-blue-700 dark:text-blue-400">
            <Home className="h-4 w-4" />
            <span className="font-medium">Welcome!</span>
          </div>
          <p className="text-sm text-blue-600 dark:text-blue-300">
            You're joining the <strong>{invitation.household_name}</strong>{" "}
            household
          </p>
          <p className="mt-2 text-xs text-blue-500 dark:text-blue-400">
            {isSignUp
              ? "Create your account to join this household and login"
              : "Sign in with your existing account to join"}
          </p>
        </div>
      )}

      {isForgotPassword ? (
        // Forgot Password View
        resetEmailSent ? (
          <div className="py-4">
            <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/30">
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                ✓ Password reset email sent!
              </p>
              <p className="mt-2 text-sm text-green-700 dark:text-green-400">
                If an account exists with this email, you'll receive a password
                reset link shortly. Check your inbox and spam folder.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                handleViewChange("signin");
                setResetEmailSent(false);
                setEmail("");
              }}
              className="w-full rounded-lg bg-blue-600 py-2 font-medium text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              Return to Sign In
            </button>
          </div>
        ) : (
          <>
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              Enter your email address and we'll send you a link to reset your
              password. The link will expire in 1 hour.
            </p>

            <form
              onSubmit={handleForgotPassword}
              className="space-y-4"
              noValidate
            >
              <div>
                <label
                  htmlFor="email"
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                    if (formErrors.email || formErrors.global) {
                      setFormErrors({});
                    }
                  }}
                  autoComplete="email"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 disabled:pointer-events-none disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  aria-describedby={
                    formErrors.email
                      ? "email-error"
                      : formErrors.global
                        ? errorId
                        : undefined
                  }
                  aria-invalid={!!formErrors.email || !!formErrors.global}
                  required
                  disabled={loading}
                />
                {formErrors.email && (
                  <p
                    id="email-error"
                    className="mt-1 text-sm text-red-700 dark:text-red-400"
                  >
                    {formErrors.email}
                  </p>
                )}
              </div>

              {formErrors.global && (
                <div
                  id={errorId}
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400"
                >
                  {formErrors.global}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !isValidEmail(email)}
                className="w-full rounded-lg bg-blue-600 py-2 font-medium text-white transition-opacity hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-700 dark:bg-blue-900/30">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                🔒 For your protection, we'll only send a reset link if the
                email exists in our system.
              </p>
            </div>

            <button
              type="button"
              onClick={() => handleViewChange("signin")}
              className="mt-6 w-full rounded-sm text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Back to Sign In
            </button>
          </>
        )
      ) : emailConfirmationPending ? (
        // Email Confirmation Pending View
        <div className="py-4">
          <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/30">
            <p className="text-sm font-medium text-green-800 dark:text-green-300">
              ✓ Confirm your email to get started!
            </p>
            <p className="mt-2 text-sm text-green-700 dark:text-green-400">
              We sent a confirmation link to <strong>{email}</strong>. Click it
              to activate your account.
            </p>
            <p className="mt-2 text-xs text-green-600 dark:text-green-500">
              Be sure to check your spam folder if you don't see it within a few
              minutes.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setEmailConfirmationPending(false);
              handleViewChange("signin");
              setPassword("");
            }}
            className="w-full rounded-lg bg-blue-600 py-2 font-medium text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            Back to Sign In
          </button>
        </div>
      ) : (
        // Sign In / Sign Up View
        <>
          <form onSubmit={handleAuth} className="space-y-4" noValidate>
            <div>
              <label
                htmlFor="email"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Email
              </label>
              <input
                id="email"
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                  if (formErrors.email || formErrors.global) {
                    setFormErrors((prev) => clearFieldError(prev, "email"));
                  }
                }}
                autoComplete="email"
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 disabled:pointer-events-none disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                aria-describedby={
                  formErrors.email
                    ? "email-error"
                    : formErrors.global
                      ? errorId
                      : undefined
                }
                aria-invalid={!!formErrors.email || !!formErrors.global}
                required
                disabled={!!invitation}
              />
              {formErrors.email && (
                <p
                  id="email-error"
                  className="mt-1 text-sm text-red-700 dark:text-red-400"
                >
                  {formErrors.email}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setPassword(e.target.value);
                    if (formErrors.password || formErrors.global) {
                      setFormErrors((prev) =>
                        clearFieldError(prev, "password"),
                      );
                    }
                  }}
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 pr-10 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  aria-describedby={
                    formErrors.password
                      ? "password-error"
                      : formErrors.global
                        ? errorId
                        : undefined
                  }
                  aria-invalid={!!formErrors.password || !!formErrors.global}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-1/2 right-2 -translate-y-1/2 rounded-sm p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p
                  id="password-error"
                  className="mt-1 text-sm text-red-700 dark:text-red-400"
                >
                  {formErrors.password}
                </p>
              )}
            </div>

            {formErrors.global && (
              <div
                id={errorId}
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400"
              >
                {formErrors.global}
              </div>
            )}

            {!Capacitor.isNativePlatform() && (
              <div style={{ display: "none" }}>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onExpire={() => {
                    setTurnstileToken(null);
                    turnstileRef.current?.reset();
                  }}
                  onError={() => {
                    setTurnstileToken(null);
                    turnstileRef.current?.reset();
                  }}
                  options={{ size: "invisible", refreshExpired: "auto" }}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={
                loading ||
                !isValidEmail(email) ||
                (!Capacitor.isNativePlatform() && !turnstileToken)
              }
              className="w-full rounded-lg bg-blue-600 py-2 font-medium text-white transition-opacity hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {loading
                ? "Loading..."
                : !Capacitor.isNativePlatform() && !turnstileToken
                  ? "Security check..."
                  : invitation
                    ? isSignUp
                      ? "Create Account & Join"
                      : "Sign In & Join"
                    : isSignUp
                      ? "Sign Up"
                      : "Sign In"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => handleViewChange(isSignUp ? "signin" : "signup")}
            className="mt-6 w-full rounded-sm text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            {isSignUp
              ? "Already have an account? Sign In"
              : "Don't have an account? Sign Up"}
          </button>

          {!isSignUp && (
            <button
              type="button"
              onClick={() => {
                handleViewChange("forgotPassword");
                setResetEmailSent(false);
              }}
              className="mt-2 w-full rounded-sm text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Forgot password?
            </button>
          )}
        </>
      )}
    </div>
  );

  return card;
}
